import { IonCard, IonCardContent, IonCardHeader, IonCardSubtitle, IonCardTitle, IonIcon, IonPopover, IonContent, IonList, IonItem, IonLabel, useIonModal, IonNote } from '@ionic/react';
import { useEffect } from 'react';
import { arrowForward, eyeOffOutline, filterOutline, home } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import styles from './ActivityOverviewTile.module.css';
import RfidProfileModal from '../modals/RfidProfileModal';
import Timestamp from './Timestamp';
import TransitDirectionIcon from './TransitDirectionIcon';
import { getRfidDisplayLabel } from '../utils/rfidDisplay';
import { eventModalHref } from '../utils/eventModalLocation';
import { useComponentTranslation } from '../hooks/useTranslation';
import type { RfidLastSeenLocation } from '../models/RfidLastSeen';

interface ActivityOverviewTileProps {
    name?: string;
    status?: string;
    timestamp?: Date | null;
    icon?: string;
    posterFrameSrc?: string;
    /** Profile avatar photo; preferred over the event poster frame when set. */
    avatarSrc?: string;
    isInside?: boolean;
    outcomeDirection?: 'inward' | 'outward' | 'peek-inward' | 'peek-outward' | 'deny-inward' | 'deny-outward';
    location?: RfidLastSeenLocation | null;
    rfidCode?: string;
    deviceId: string;
    eventId?: number;
    showViewEventAction?: boolean;
    shortTimestamp?: boolean;
    onShowEventsClick?: (rfidCode: string) => void;
    onSetLocationClick?: (location: RfidLastSeenLocation) => void;
    onHideClick?: () => void;
    isSetLocationPending?: boolean;
    isHidePending?: boolean;
}

const ActivityOverviewTile: React.FC<ActivityOverviewTileProps> = ({
    name,
    status,
    timestamp,
    icon,
    posterFrameSrc,
    avatarSrc,
    isInside,
    outcomeDirection,
    location,
    rfidCode,
    deviceId,
    eventId,
    showViewEventAction = true,
    shortTimestamp,
    onShowEventsClick,
    onSetLocationClick,
    onHideClick,
    isSetLocationPending,
    isHidePending
}) => {
    const statusClass = isInside ? styles.inside : styles.outside;
    const petImageSrc = avatarSrc ?? posterFrameSrc;
    const tileId = `activity-tile-${deviceId}-${rfidCode ?? eventId ?? 'unknown'}`.replace(/[^A-Za-z0-9_-]/g, '-');
    const history = useHistory();
    const { t } = useComponentTranslation();
    const displayName = rfidCode ? getRfidDisplayLabel(rfidCode, name) : name;
    const canViewEvent = showViewEventAction && !!eventId;

    const [presentProfileModal, dismissProfileModal] = useIonModal(RfidProfileModal, {
        dismiss: (data: string, role: string) => dismissProfileModal(data, role),
        rfidCode
    });

    const handleProfileClick = () => {
        if (rfidCode) {
            presentProfileModal({
                presentingElement: document.getElementsByTagName('ion-router-outlet')[0],
                cssClass: 'oc-dialog-m'
            });
        }
    };

    // Screenshot builds only: ?petProfile=<rfidCode> deep-links straight into
    // the profile modal, so the capture harness can reach it without clicking
    // through the popover. Dead code in production builds.
    useEffect(() => {
        if (import.meta.env.MODE !== 'screenshot' || !rfidCode) {
            return;
        }
        if (new URLSearchParams(window.location.search).get('petProfile') === rfidCode) {
            handleProfileClick();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rfidCode]);

    const handleViewEventClick = () => {
        if (eventId) {
            history.replace(eventModalHref(history.location, deviceId, eventId));
        }
    };

    const handleShowEventsClick = () => {
        if (rfidCode) {
            onShowEventsClick?.(rfidCode);
        }
    };

    const nextLocation: RfidLastSeenLocation = location === 'INSIDE' ? 'OUTSIDE' : 'INSIDE';

    const handleSetLocationClick = () => {
        onSetLocationClick?.(nextLocation);
    };

    const handleHideClick = () => {
        onHideClick?.();
    };

    return (
        <>
            <IonCard id={tileId} className={`${styles.tile} ${statusClass}`} mode='ios'>
                <div className={styles.iconContainer}>
                    {petImageSrc
                        ? <img src={petImageSrc} alt="" className={`${styles.petAvatar} ${styles.petAvatarImage}`} />
                        : <IonIcon src={icon} className={styles.petAvatar} />}
                    {outcomeDirection && (
                        <TransitDirectionIcon
                            direction={outcomeDirection}
                            className={styles.statusOverlay}
                        />
                    )}
                </div>
                <IonCardHeader>
                    <IonCardTitle>{displayName}</IonCardTitle>
                    <IonCardSubtitle>{status}</IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent>
                    <IonNote><Timestamp timestamp={timestamp} short={shortTimestamp} /></IonNote>
                </IonCardContent>
            </IonCard>
            <IonPopover trigger={tileId} dismissOnSelect>
                <IonContent>
                    <IonList>
                        <IonItem button={true} detail={false} disabled={!rfidCode} onClick={handleProfileClick}>
                            <IonIcon slot="start" src="/assets/rfid/tag-simple.svg" />
                            <IonLabel>{t('activity_overview_tile.pet_profile')}</IonLabel>
                        </IonItem>

                        <IonItem button={true} detail={false} disabled={!rfidCode || !onSetLocationClick || isSetLocationPending} onClick={handleSetLocationClick}>
                            <IonIcon slot="start" icon={nextLocation === 'INSIDE' ? home : arrowForward} />
                            <IonLabel>{t(nextLocation === 'INSIDE' ? 'activity_overview_tile.mark_inside' : 'activity_overview_tile.mark_outside')}</IonLabel>
                        </IonItem>

                        {canViewEvent && (
                            <IonItem button={true} detail={false} onClick={handleViewEventClick}>
                                <IonIcon slot="start" src={icon} />
                                <IonLabel>{t('activity_overview_tile.view_event')}</IonLabel>
                            </IonItem>
                        )}

                        <IonItem button={true} detail={false} disabled={!rfidCode || !onShowEventsClick} onClick={handleShowEventsClick}>
                            <IonIcon slot="start" icon={filterOutline} />
                            <IonLabel>{t('activity_overview_tile.show_events')}</IonLabel>
                        </IonItem>

                        <IonItem button={true} detail={false} disabled={!onHideClick || isHidePending} onClick={handleHideClick}>
                            <IonIcon slot="start" icon={eyeOffOutline} color="danger" />
                            <IonLabel color="danger">{t('activity_overview_tile.hide_visitor')}</IonLabel>
                        </IonItem>
                    </IonList>
                </IonContent>
            </IonPopover>
        </>
    );
};

export default ActivityOverviewTile;
